import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount: number, currency: string = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2
  }).format(amount)
}

/**
 * Gets UTM parameters from URL and returns them as an object
 */
export function getUtmParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};
  
  // Extract standard UTM parameters
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
    const value = params.get(param);
    if (value) {
      // Check if the value contains an encoded UTM parameter (which would indicate double-encoding)
      if (value.includes('%3Futm_') || value.includes('?utm_')) {
        // This is a case where UTM params got incorrectly nested - extract and clean them
        try {
          // Try to extract the actual UTM values
          const cleanValue = value.replace(/%3Futm_/g, '?utm_').split('?utm_')[0];
          utmParams[param] = cleanValue;
        } catch (e) {
          // If anything goes wrong, use the original value
          utmParams[param] = value;
        }
      } else {
        // Normal case - just use the value directly
        utmParams[param] = value;
      }
    }
  });
  
  return utmParams;
}

/**
 * Adds UTM parameters to a URL if they exist in the current URL
 */
export function addUtmParamsToUrl(url: string): string {
  const utmParams = getUtmParams();
  
  if (Object.keys(utmParams).length === 0) {
    return url;
  }
  
  // Parse the destination URL
  const urlObj = new URL(url, window.location.origin);
  const searchParams = new URLSearchParams(urlObj.search);
  
  // Add UTM parameters to the URL, making sure we're not re-encoding values
  Object.entries(utmParams).forEach(([key, value]) => {
    // Remove any URL encoding from the value to prevent double-encoding
    searchParams.set(key, decodeURIComponent(value));
  });
  
  urlObj.search = searchParams.toString();
  return urlObj.toString();
}

/**
 * Formats a number with comma separators
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

/**
 * Formats a date string into a readable format
 */
export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}
